/** SUNTRONIX ONE — Commercial Lifecycle strip.
 *
 *  A 6-stage horizontal indicator used on Quote / Customer PO / Order
 *  detail views to show which commercial stage the record currently sits
 *  in.  Every reached stage is a clickable back-link to the associated
 *  record (open in a new context).
 *
 *  Stages: QUOTATION → CUSTOMER PO → ONE ORDER → PROCUREMENT →
 *          DEPLOYMENT / DELIVERY → INVOICE
 */
import { Link } from "react-router-dom";
import { Check, Circle, DotIcon } from "lucide-react";

const STAGES = [
  { key: "quotation",    label: "Quotation",   linkFrom: (r) => r.quote_id  || r.quotation_id  ? `/commercial/crm/quotations?open=${r.quote_id || r.quotation_id}` : null },
  { key: "customer_po", label: "Customer PO", linkFrom: (r) => r.customer_po_id ? `/commercial/customer-po?open=${r.customer_po_id}` : null },
  { key: "one_order",    label: "ONE Order",   linkFrom: (r) => r.one_order_id  ? `/commercial/one-orders?open=${r.one_order_id}` : null },
  { key: "procurement", label: "Procurement", linkFrom: (r) => r.vendor_po_id ? `/commercial/crm/vendor-pos?open=${r.vendor_po_id}` : null },
  { key: "delivery",       label: "Delivery",     linkFrom: (r) => r.shipment_id ? `/shipments?open=${r.shipment_id}` : null },
  { key: "invoice",         label: "Invoice",       linkFrom: (r) => r.invoice_id   ? `/invoices?open=${r.invoice_id}` : null },
];

export default function CommercialLifecycleStrip({ record, currentStage }) {
  const r = record || {};
  const currentIdx = Math.max(0, STAGES.findIndex((s) => s.key === currentStage));
  return (
    <div className="border border-slate-200 rounded-md p-2 flex items-center gap-1 overflow-x-auto"
        data-testid="commercial-lifecycle-strip">
      {STAGES.map((s, i) => {
        const done      = i < currentIdx;
        const active   = i === currentIdx;
        const link       = s.linkFrom(r);
        const tone       = done
                                        ? "text-emerald-700 bg-emerald-50 ring-emerald-200"
                                        : active
                                            ? "text-slate-900 bg-white ring-slate-300"
                                            : "text-slate-400 bg-slate-50 ring-slate-200";
        const chip = (
          <span className={"text-[10px] px-1.5 py-0.5 rounded ring-1 inline-flex items-center gap-1 " + tone}
              data-testid={`lifecycle-${s.key}`}>
            {done ? <Check className="h-2.5 w-2.5" />
                       : active ? <Circle className="h-2 w-2 fill-current" />
                       : <DotIcon className="h-2.5 w-2.5" />}
            {s.label}
          </span>
        );
        return (
          <div key={s.key} className="flex items-center gap-1 shrink-0">
            {link
                ? <Link to={link} data-testid={`lifecycle-link-${s.key}`}>{chip}</Link>
                : chip}
            {i < STAGES.length - 1 &&
                <div className={"h-px w-3 " + (done ? "bg-emerald-300" : "bg-slate-200")} />}
          </div>
        );
      })}
    </div>
  );
}
